import { Button, Popconfirm, message, InputNumber, Input, Modal, Select } from "antd";
import {
  ClearOutlined,
  PlusCircleOutlined,
  MinusCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import {
  deleteProduct,
  increase,
  decrease,
  setQuantity,
  updateItemPrice,
  updateItemTitle,
  updateItemDiscount,
  reset,
  addCartTab,
  removeCartTab,
  setActiveCartTab,
  selectActiveCart,
  selectActiveTabId,
  selectCartTabs,
  addProduct,
  setPaymentMode,
} from "../../redux/cartSlice";
import { useNavigate } from "react-router-dom";
import { useEffect, useState, useRef, useCallback } from "react";
import { CloseOutlined, PictureOutlined, BarcodeOutlined } from "@ant-design/icons";
import { apiRequest } from "../../utils/apiClient";
import checkPosType from "../../utils/posType";
import { useMemo } from "react";
import {
  CART_TOTALS_MIN_WIDTH,
  CART_TOTALS_MAX_WIDTH,
  CART_TOTALS_DEFAULT_WIDTH,
  INVOICE_TYPE_POS,
  INVOICE_TYPE_DIGITAL_INVOICING,
  PAYMENT_MODE_CASH,
  PAYMENT_MODE_CREDIT,
  PAYMENT_MODE_DEBIT_CARD,
} from "../../utils/constants";
import { useTranslation } from "react-i18next";

const CartTotals = () => {
  const { t } = useTranslation("pages");
  const { t: tCommon } = useTranslation("common");
  const dispatch = useDispatch();
  const activeCart = useSelector(selectActiveCart);
  const cartItems = activeCart?.cartItems ?? [];
  const selectedPaymentMode = activeCart?.paymentMode ?? PAYMENT_MODE_CASH;
  const paymentModeTouched = activeCart?.paymentModeTouched ?? false;
  const activeTabId = useSelector(selectActiveTabId);
  const cartTabs = useSelector(selectCartTabs);
  const navigate = useNavigate();
  const [editingQuantity, setEditingQuantity] = useState(null);
  const [editingTitleId, setEditingTitleId] = useState(null);
  const [titleDraft, setTitleDraft] = useState("");
  
  // Resize functionality
  const [width, setWidth] = useState(() => {
    const saved = localStorage.getItem("cartTotalsWidth");
    return saved ? parseInt(saved, 10) : CART_TOTALS_DEFAULT_WIDTH;
  });
  const [isResizing, setIsResizing] = useState(false);
  const isResizingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const prevCartItemIdsRef = useRef([]);
  const prevActiveTabIdRef = useRef(activeTabId);
  const barcodeInputRef = useRef(null);

  // Barcode scan: look up product by barcode and add to cart
  const [barcodeValue, setBarcodeValue] = useState("");
  const [barcodeLoading, setBarcodeLoading] = useState(false);

  const handleBarcodeSubmit = useCallback(async () => {
    const barcode = String(barcodeValue || "").trim();
    if (!barcode) return;

    setBarcodeLoading(true);
    setBarcodeValue("");
    try {
      let url = `/api/products/by-barcode?barcode=${encodeURIComponent(barcode)}`;
      const effectiveBranchId = user?.branchId || user?.selectedBranchId;
      if (effectiveBranchId) url += `&branchId=${encodeURIComponent(effectiveBranchId)}`;
      const data = await apiRequest(url);
      const product = data?.product;
      if (!product?._id) {
        message.warning(t("barcodeNotFound"));
        return;
      }
      dispatch(addProduct(product));
    } catch (err) {
      if (err?.status === 404 || err?.message?.toLowerCase?.().includes("not found")) {
        message.warning(t("barcodeNotFound"));
      } else {
        message.error(err?.message || t("barcodeLookupFailed"));
      }
    } finally {
      setBarcodeLoading(false);
      barcodeInputRef.current?.focus();
    }
  }, [barcodeValue, dispatch, t]);

  // Listen for global shortcut to focus barcode input (e.g. key "2")
  useEffect(() => {
    const handleFocusBarcode = () => {
      barcodeInputRef.current?.focus();
    };
    window.addEventListener("focusBarcodeInput", handleFocusBarcode);
    return () => window.removeEventListener("focusBarcodeInput", handleFocusBarcode);
  }, []);

  // const [price, setPrice] = useState({
  //   packPrice: 0,
  //   packQty: 0,
  //   discount: 0,
  // });

  // const handlePackPriceChange = (value) => {
  //   setPrice((prev) => ({ ...prev, packPrice: value }));
  // };
  // const handlePackQtyChange = (value) => {
  //   setPrice((prev) => ({ ...prev, packQty: value }));
  // };
  // const handleDiscountChange = (value) => {
  //   setPrice((prev) => ({ ...prev, discount: value }));
  // };

  // const handlePriceChange = (value) => {
  //   setPrice((prev) => ({ ...prev, price: value }));
  // };

  // console.log({ price });

  const user = useSelector((state) => state.user);
  
  const { posType, invoiceType } = useMemo(() => {
    const businessName = user?.businessName || "";
    const invType = user?.invoiceType || null;
    return { 
      posType: checkPosType(businessName),
      invoiceType: invType
    };
  }, [user]);

  // Show discount field only for POS (not DIGITAL_INVOICING)
  const showDiscountField = invoiceType !== INVOICE_TYPE_DIGITAL_INVOICING;

  // Digital Invoicing default: Payment Method = Credit (only if user hasn't changed it)
  useEffect(() => {
    if (invoiceType !== INVOICE_TYPE_DIGITAL_INVOICING) return;
    if (paymentModeTouched) return;
    if (selectedPaymentMode === PAYMENT_MODE_CREDIT) return;
    dispatch(setPaymentMode({ paymentMode: PAYMENT_MODE_CREDIT, touched: false }));
  }, [invoiceType, paymentModeTouched, selectedPaymentMode, dispatch]);

  // track user-entered prices for products with price = 0
  const [priceInputs, setPriceInputs] = useState({});

  // track user-entered discounts per product (only for POS)
  const [discountInputs, setDiscountInputs] = useState({});

  // keep local price inputs aligned with cart items (adds new, removes stale)
  useEffect(() => {
    setPriceInputs((prev) => {
      const next = { ...prev };

      cartItems.forEach((item) => {
        if (next[item._id] === undefined) {
          // In the cart, `item.price` is treated as **unit price**.
          const numericPrice = Number(item.price);
          next[item._id] = Number.isFinite(numericPrice)
            ? Number(numericPrice.toFixed(2))
            : null;
        }
      });

      Object.keys(next).forEach((id) => {
        const stillExists = cartItems.some((item) => item._id === id);
        if (!stillExists) {
          delete next[id];
        }
      });

      return next;
    });
  }, [cartItems]);

  // keep local discount inputs aligned with cart items (adds new, removes stale)
  useEffect(() => {
    setDiscountInputs((prev) => {
      const next = { ...prev };

      cartItems.forEach((item) => {
        if (next[item._id] === undefined) {
          next[item._id] = item.discount ?? 0;
        }
      });

      Object.keys(next).forEach((id) => {
        const stillExists = cartItems.some((item) => item._id === id);
        if (!stillExists) {
          delete next[id];
        }
      });

      return next;
    });
  }, [cartItems]);

  const sanitizePrice = (val) => {
    const num = Number(val);
    if (!Number.isFinite(num) || num < 0) return 0;
    return Number(num.toFixed(2));
  };

  const sanitizeDiscountPercent = (val) => {
    const num = Number(val);
    if (!Number.isFinite(num) || num < 0) return 0;
    return Math.min(100, Math.max(0, Number(num.toFixed(2)))); // Clamp between 0-100
  };

  // Calculate discount amount from percentage
  const calculateDiscountAmount = (price, quantity, discountPercent) => {
    const itemSubtotal = price * quantity;
    return (itemSubtotal * discountPercent) / 100;
  };

  // Calculate totals safely based on cart items (with discounts applied)
  const subTotal = cartItems.reduce(
    (sum, item) => {
      const price = priceInputs[item._id] ?? item.price ?? 0;
      const quantity = item.quantity || 0;
      const discountPercent = discountInputs[item._id] ?? item.discount ?? 0;
      const itemSubtotal = price * quantity;
      const itemDiscountAmount = calculateDiscountAmount(price, quantity, discountPercent);
      return sum + (itemSubtotal - itemDiscountAmount);
    },
    0
  );

  const totalDiscount = cartItems.reduce(
    (sum, item) => {
      const price = priceInputs[item._id] ?? item.price ?? 0;
      const quantity = item.quantity || 0;
      const discountPercent = discountInputs[item._id] ?? item.discount ?? 0;
      const itemDiscountAmount = calculateDiscountAmount(price, quantity, discountPercent);
      return sum + itemDiscountAmount;
    },
    0
  );

  // Tax logic:
  // - POS + PRA integration (checkPosType true) + Debit Card => 5% tax on subtotal
  // - otherwise => sum item-level taxes
  const totalTax =
    invoiceType === INVOICE_TYPE_POS &&
    posType === true &&
    selectedPaymentMode === PAYMENT_MODE_DEBIT_CARD
      ? (subTotal * 5) / 100
      : cartItems.reduce((sum, item) => sum + (item.tax || 0), 0);

  const grandTotal = subTotal + totalTax;

  // persist title change for a single item (updates redux + backend)
  const commitTitleChange = async (item, newTitleRaw) => {
    const newTitle = String(newTitleRaw || "").trim();
    const currentTitle = String(item.title || "").trim();

    setEditingTitleId(null);

    if (!newTitle || newTitle === currentTitle) {
      return;
    }

    // optimistic update in cart
    dispatch(updateItemTitle({ productId: item._id, title: newTitle }));

    try {
      await apiRequest("/api/products/update-product", {
        method: "PUT",
        body: JSON.stringify({
          productId: item._id,
          title: newTitle,
        }),
      });
      message.success(t("productNameUpdated"));

      // Dispatch custom event to notify Products component to update its state
      window.dispatchEvent(
        new CustomEvent("productUpdated", {
          detail: { productId: item._id, title: newTitle },
        })
      );
    } catch (err) {
      console.error(err);
      message.error(t("productNameUpdateFailed"));
      // rollback redux to previous title
      dispatch(updateItemTitle({ productId: item._id, title: currentTitle }));
    }
  };

  // persist price change for a single item (updates redux + backend)
  const commitPriceChange = async (item, rawValue) => {
    const newPrice = sanitizePrice(rawValue);
    setPriceInputs((prev) => ({ ...prev, [item._id]: newPrice }));

    // const currentPrice = item.price ?? 0;
    // if (newPrice === currentPrice) return;

    // // optimistic update cart totals/tax
    // dispatch(updateItemPrice({ productId: item._id, price: newPrice }));

    // try {
    //   await apiRequest("/api/products/update-product", {
    //     method: "PUT",
    //     body: JSON.stringify({
    //       productId: item._id,
    //       price: newPrice,
    //     }),
    //   });
    //   message.success("Price updated");
    // } catch (err) {
    //   console.error(err);
    //   message.error("Failed to update price. Reverting.");
    //   // rollback redux + input to the previous price
    //   dispatch(updateItemPrice({ productId: item._id, price: currentPrice }));
    //   setPriceInputs((prev) => ({ ...prev, [item._id]: currentPrice }));
    // }
  };


  // update any edited prices before proceeding to invoice
  // const updatePricesBeforeInvoice = async () => {
  //   const productsToUpdate = cartItems
  //     .map((item) => {
  //       const entered = priceInputs[item._id];
  //       const newPrice = sanitizePrice(
  //         entered === undefined ? item.price : entered
  //       );
  //       return { item, newPrice };
  //     })
  //     .filter(
  //       ({ item, newPrice }) => newPrice !== sanitizePrice(item.price ?? 0)
  //     );

  //   try {
  //     if (productsToUpdate.length > 0) {
  //       await Promise.all(
  //         productsToUpdate.map(({ item, newPrice }) =>
  //           apiRequest("/api/products/update-product", {
  //             method: "PUT",
  //             body: JSON.stringify({
  //               productId: item._id,
  //               price: newPrice,
  //             }),
  //           })
  //         )
  //       );
  //       // keep cart totals/tax in sync after backend update
  //       productsToUpdate.forEach(({ item, newPrice }) => {
  //         dispatch(updateItemPrice({ productId: item._id, price: newPrice }));
  //       });
  //       message.success("Prices updated successfully.");
  //     }
  //   } catch (err) {
  //     console.error(err);
  //     message.error("Failed to update prices. Try again.");
  //     return false;
  //   }

  //   return true;
  // };

  const syncPricesAndDiscounts = useCallback(() => {
    if (priceInputs) {
      Object.keys(priceInputs).forEach((id) => {
        const enteredPrice = priceInputs[id];
        if (enteredPrice > 0) {
          dispatch(updateItemPrice({ productId: id, price: enteredPrice }));
        }
      });
    }
    if (discountInputs) {
      Object.keys(discountInputs).forEach((id) => {
        const enteredDiscount = discountInputs[id] ?? 0;
        dispatch(updateItemDiscount({ productId: id, discount: enteredDiscount }));
      });
    }
  }, [priceInputs, discountInputs, dispatch]);

  const handleCreateInvoice = useCallback(() => {
    syncPricesAndDiscounts();
    const shouldAutoSubmit = invoiceType === INVOICE_TYPE_POS;
    const url = shouldAutoSubmit
      ? "/cart?checkout=1&autoSubmit=1"
      : "/cart?checkout=1";
    navigate(url);
  }, [syncPricesAndDiscounts, navigate, invoiceType, posType]);

  const handleGoToCartAndCreateInvoice = useCallback(() => {
    syncPricesAndDiscounts();
    navigate("/cart?checkout=1&focusCustomer=1");
  }, [syncPricesAndDiscounts, navigate]);

  // Listen for keyboard shortcut "0" to create invoice
  useEffect(() => {
    const handler = () => {
      if (cartItems.length > 0) handleCreateInvoice();
    };
    window.addEventListener("createInvoiceShortcut", handler);
    return () => window.removeEventListener("createInvoiceShortcut", handler);
  }, [cartItems.length, handleCreateInvoice]);

  // Listen for keyboard shortcut "9" to go to basket then open create-invoice modal
  useEffect(() => {
    const handler = () => {
      if (cartItems.length > 0) handleGoToCartAndCreateInvoice();
    };
    window.addEventListener("goToCartAndCreateInvoice", handler);
    return () => window.removeEventListener("goToCartAndCreateInvoice", handler);
  }, [cartItems.length, handleGoToCartAndCreateInvoice]);

  // Resize handlers
  const handleMouseMove = useCallback((e) => {
    if (!isResizingRef.current) return;
    const diff = startXRef.current - e.clientX; // Resizing from left (drag left = wider, drag right = narrower)
    const newWidth = Math.max(CART_TOTALS_MIN_WIDTH, Math.min(CART_TOTALS_MAX_WIDTH, startWidthRef.current + diff));
    setWidth(newWidth);
  }, []);

  const handleMouseUp = useCallback(() => {
    if (!isResizingRef.current) return;
    isResizingRef.current = false;
    setIsResizing(false);
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  }, [handleMouseMove]);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    isResizingRef.current = true;
    setIsResizing(true);
    startXRef.current = e.clientX;
    startWidthRef.current = width;
    
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, [width, handleMouseMove, handleMouseUp]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [handleMouseMove, handleMouseUp]);

  // Save width to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("cartTotalsWidth", width.toString());
  }, [width]);

  // Auto-focus quantity input only when a new item is added on the *same* tab (not when switching tabs)
  useEffect(() => {
    const prevIds = prevCartItemIdsRef.current || [];
    const currentIds = cartItems.map((item) => item._id);
    const tabChanged = prevActiveTabIdRef.current !== activeTabId;
    prevActiveTabIdRef.current = activeTabId;

    if (tabChanged) {
      prevCartItemIdsRef.current = currentIds;
      setEditingQuantity(null);
      return;
    }

    const newIds = currentIds.filter((id) => !prevIds.includes(id));

    if (newIds.length > 0) {
      const lastNewId = newIds[newIds.length - 1];
      setEditingQuantity(lastNewId);
    }

    prevCartItemIdsRef.current = currentIds;
  }, [cartItems, activeTabId]);

  return (
    <div
      className="cart h-full flex flex-col bg-gray-50 relative"
      style={{ width: `${width}px`, overflowY: "hidden" }}
    >
      {/* Elegant resize handle - positioned on left edge */}
      <div
        onMouseDown={handleMouseDown}
        className="absolute top-0 bottom-0 cursor-col-resize z-50 transition-colors duration-200"
        style={{ 
          touchAction: "none",
          left: "0px",
          width: "5px",
          pointerEvents: "auto",
          backgroundColor: isResizing ? "rgba(59, 130, 246, 0.15)" : "transparent"
        }}
        onMouseEnter={(e) => {
          if (!isResizing) {
            e.currentTarget.style.backgroundColor = "rgba(59, 130, 246, 0.1)";
          }
        }}
        onMouseLeave={(e) => {
          if (!isResizing) {
            e.currentTarget.style.backgroundColor = "transparent";
          }
        }}
        title={t("dragToResize")}
      >
        {/* Elegant three-dot grip indicator */}
        <div
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-1.5 transition-all duration-200"
          style={{
            opacity: isResizing ? 1 : 0.4
          }}
        >
          <div 
            className="w-1.5 h-1.5 rounded-full transition-all duration-200 shadow-sm"
            style={{
              backgroundColor: isResizing ? "#3b82f6" : "#94a3b8",
              boxShadow: isResizing ? "0 0 4px rgba(59, 130, 246, 0.4)" : "none"
            }}
          />
          <div 
            className="w-1.5 h-1.5 rounded-full transition-all duration-200 shadow-sm"
            style={{
              backgroundColor: isResizing ? "#3b82f6" : "#94a3b8",
              boxShadow: isResizing ? "0 0 4px rgba(59, 130, 246, 0.4)" : "none"
            }}
          />
          <div 
            className="w-1.5 h-1.5 rounded-full transition-all duration-200 shadow-sm"
            style={{
              backgroundColor: isResizing ? "#3b82f6" : "#94a3b8",
              boxShadow: isResizing ? "0 0 4px rgba(59, 130, 246, 0.4)" : "none"
            }}
          />
        </div>
      </div>
      {/* Header */}
      <h2 className="flex items-center justify-between bg-blue-600 px-4 py-3 text-base font-medium tracking-wide text-white shrink-0">
        <span className="text-white">{t("basketTitle")}</span>
        <span className="text-xs font-normal opacity-80 text-white">
          {t("itemsCount", { count: cartItems.length })}
        </span>
      </h2>

      {/* Basket tabs: switch or add new invoice tab */}
      <div className="basket-tabs shrink-0 flex items-center gap-1 px-2 py-2 border-b border-gray-200 bg-gray-50/80">
        <div className="flex items-center gap-1 min-w-0 flex-1 overflow-x-auto">
          {cartTabs.map((tab, index) => {
            const isActive = tab.id === activeTabId;
            const itemCount = (tab.cartItems || []).length;
            return (
              <div
                key={tab.id}
                className="flex items-center shrink-0 rounded-lg border overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => dispatch(setActiveCartTab(tab.id))}
                  className={`
                    flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium transition-colors
                    ${isActive
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-gray-700 border-gray-200 hover:bg-gray-100 hover:border-gray-300"
                    }
                  `}
                  title={t("basketTabTitle", { index: index + 1, count: itemCount })}
                >
                  <span className="truncate max-w-[9ch]">{t("billTabLabel", { number: index + 1 })}</span>
                  {itemCount > 0 && (
                    <span className={isActive ? "text-blue-100" : "text-gray-500"}>
                      ({itemCount})
                    </span>
                  )}
                </button>
                {cartTabs.length > 1 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (itemCount > 0) {
                        Modal.confirm({
                          title: t("closeBasketTabTitle"),
                          content: t("closeBasketTabConfirm"),
                          okText: tCommon("yes"),
                          cancelText: tCommon("no"),
                          onOk: () => dispatch(removeCartTab(tab.id)),
                        });
                      } else {
                        dispatch(removeCartTab(tab.id));
                      }
                    }}
                    className={`
                      flex items-center justify-center w-6 h-7 text-[10px] transition-colors
                      ${isActive ? "text-blue-100 hover:bg-blue-700 hover:text-white" : "text-gray-400 hover:bg-gray-200 hover:text-gray-700"}
                    `}
                    title={t("closeBasketTab")}
                  >
                    <CloseOutlined />
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <button
          type="button"
          onClick={() => dispatch(addCartTab())}
          className="flex items-center justify-center w-8 h-8 shrink-0 rounded-lg border border-dashed border-gray-300 bg-white text-gray-500 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50/50 transition-colors"
          title={t("newBasketTab")}
        >
          <PlusOutlined className="text-sm" />
        </button>
      </div>

      {/* Barcode scan: scan or type barcode, press Enter to add item */}
      <div className="shrink-0 px-2 pb-2">
        <div className="flex items-center gap-2">
          <Input
            ref={barcodeInputRef}
            size="small"
            placeholder={t("scanBarcodePlaceholder")}
            prefix={<BarcodeOutlined className="text-gray-400" />}
            value={barcodeValue}
            onChange={(e) => setBarcodeValue(e.target.value)}
            onPressEnter={handleBarcodeSubmit}
            disabled={barcodeLoading}
            className="flex-1"
            allowClear
          />
          <Button
            type="primary"
            size="small"
            loading={barcodeLoading}
            onClick={handleBarcodeSubmit}
            title={t("scanBarcodePlaceholder")}
          >
            {t("addItem")}
          </Button>
        </div>
      </div>

      {/* Cart items list */}
      <ul className="cart-items flex min-h-0 flex-1 flex-col gap-y-3 overflow-y-auto px-3 pt-3 pb-3">
        {cartItems.length > 0 ? (
          cartItems
            .map((item) => (
              <li
                key={item._id}
                className="cart-item relative flex items-stretch justify-between gap-3 rounded-xl border border-gray-100 bg-white px-3 py-3 shadow-sm transition-shadow hover:shadow-md"
              >
                {/* Remove button */}
                <button
                  type="button"
                  onClick={() => dispatch(deleteProduct(item))}
                  className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-gray-200 text-[10px] text-gray-600 transition-colors hover:bg-red-500 hover:text-white"
                >
                  <CloseOutlined />
                </button>

                {/* Left: image + product info */}
                <div className="flex min-w-0 flex-1 items-start gap-3">
                  {posType && (
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-md bg-gray-100">
                      {item.img ? (
                        <img
                          src={item.img}
                          alt={item.title}
                          className="h-full w-full object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                            const parent = e.currentTarget.parentNode;
                            if (parent) {
                              parent.classList.add(
                                "flex",
                                "items-center",
                                "justify-center"
                              );
                              // Use safe DOM manipulation instead of innerHTML
                              const icon = document.createElement("span");
                              icon.className = "anticon anticon-picture";
                              icon.style.fontSize = "24px";
                              icon.style.color = "#9ca3af";
                              // Remove all child nodes safely
                              while (parent.firstChild) {
                                parent.removeChild(parent.firstChild);
                              }
                              parent.appendChild(icon);
                            }
                          }}
                        />
                      ) : (
                        <PictureOutlined className="text-2xl text-gray-400" />
                      )}
                    </div>
                  )}

                  <div className="flex min-w-0 flex-1 flex-col gap-2">
                    {/* Title + quantity summary */}
                    <div className="flex items-start justify-between gap-2">
                      {editingTitleId === item._id ? (
                        <Input
                          size="small"
                          autoFocus
                          defaultValue={item.title}
                          className="max-w-[160px] text-sm"
                          onChange={(e) => setTitleDraft(e.target.value)}
                          onBlur={() => commitTitleChange(item, titleDraft || item.title)}
                          onPressEnter={(e) =>
                            commitTitleChange(item, e.target.value || item.title)
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Escape") {
                              setEditingTitleId(null);
                            }
                          }}
                        />
                      ) : (
                        <button
                          type="button"
                          className="max-w-[180px] truncate text-left text-sm font-medium text-gray-800 hover:underline"
                          title={t("tapToEditName")}
                          onClick={() => {
                            setTitleDraft(item.title || "");
                            setEditingTitleId(item._id);
                          }}
                        >
                          {item.title}
                        </button>
                      )}
                      <span className="whitespace-nowrap text-xs font-medium text-gray-500">
                        {t("qty")}: {item.quantity ?? 0}
                      </span>
                    </div>

                    {/* { true && <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-600 w-12">Pack Rate</span>
                      <InputNumber
                        min={0}
                        value={price.packPrice}
                        placeholder="0.00"
                        className="!w-28"
                        onChange={handlePackPriceChange}
                      />
                    </div> }

                    { true && <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-600 w-12">Pack Qty</span>
                      <InputNumber
                        min={0}
                        value={price.packQty}
                        placeholder="0.00"
                        className="!w-28"
                        onChange={handlePackQtyChange}
                      />
                    </div> }

                    { true && <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-600 w-12">Discount</span>
                      <InputNumber
                        min={0}
                        value={price.discount}
                        placeholder="0.00"
                        className="!w-28"
                        onChange={handleDiscountChange}
                      />
                    </div> } */}

                    {/* Price + discount area */}
                    <div className="mt-1 flex flex-col gap-1.5 text-xs">
                      {/* Price input row */}
                      <div className="flex items-center gap-2">
                        <span className="w-16 text-[11px] font-medium text-gray-600">
                          {t("price")}
                        </span>
                        <InputNumber
                          min={0}
                          value={priceInputs[item._id]}
                          placeholder="0.00"
                          className="!w-28"
                          onChange={(val) =>
                            setPriceInputs((prev) => ({
                              ...prev,
                              [item._id]: sanitizePrice(val),
                            }))
                          }
                          onBlur={(e) => commitPriceChange(item, e.target.value)}
                          onPressEnter={(e) => commitPriceChange(item, e.target.value)}
                        />
                      </div>

                      {/* Discount input row (only for POS) - percentage based */}
                      {showDiscountField && (
                        <div className="flex items-center gap-2">
                          <span className="w-16 text-[11px] font-medium text-gray-600">
                            {t("discountPercent")}
                          </span>
                          <InputNumber
                            min={0}
                            max={100}
                            value={discountInputs[item._id] ?? 0}
                            placeholder="0"
                            className="!w-28"
                            onChange={(val) => {
                              const sanitized = sanitizeDiscountPercent(val);
                              setDiscountInputs((prev) => ({
                                ...prev,
                                [item._id]: sanitized,
                              }));
                              dispatch(
                                updateItemDiscount({
                                  productId: item._id,
                                  discount: sanitized,
                                })
                              );
                            }}
                          />
                        </div>
                      )}

                      {/* Item total summary */}
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500">
                        <span>
                          {t("lineTotal")}:{" "}
                          <span className="font-semibold text-gray-800">
                            {(
                              (priceInputs[item._id] ?? item.price ?? 0) *
                                (item.quantity || 0) -
                              calculateDiscountAmount(
                                priceInputs[item._id] ?? item.price ?? 0,
                                item.quantity || 0,
                                discountInputs[item._id] ?? item.discount ?? 0
                              )
                            ).toFixed(2)}
                          </span>
                        </span>
                        {showDiscountField &&
                          (discountInputs[item._id] ?? item.discount ?? 0) > 0 && (
                            <span className="rounded-full bg-green-50 px-2 py-0.5 text-[10px] font-medium text-green-700">
                              −
                              {calculateDiscountAmount(
                                priceInputs[item._id] ?? item.price ?? 0,
                                item.quantity || 0,
                                discountInputs[item._id] ?? item.discount ?? 0
                              ).toFixed(2)}{" "}
                              {t("discountLabel")}
                            </span>
                          )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right: quantity controls */}
                <div className="flex flex-col items-center justify-center gap-1">
                  <Button
                    type="primary"
                    size="small"
                    className="!rounded-full"
                    icon={<PlusCircleOutlined />}
                    onClick={() => dispatch(increase(item))}
                  />

                  {editingQuantity === item._id ? (
                    <InputNumber
                      min={0}
                      defaultValue={item.quantity}
                      autoFocus
                      size="small"
                      className="!w-16 text-center"
                      onPressEnter={(e) => {
                        const newQuantity = parseInt(e.target.value) || 0;
                        dispatch(setQuantity({ item, quantity: newQuantity }));
                        setEditingQuantity(null);
                      }}
                      onBlur={(e) => {
                        const newQuantity = parseInt(e.target.value) || 0;
                        dispatch(setQuantity({ item, quantity: newQuantity }));
                        setEditingQuantity(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Escape") setEditingQuantity(null);
                      }}
                    />
                  ) : (
                    <button
                      type="button"
                      className="font-semibold text-sm inline-flex items-center justify-center h-7 w-12 cursor-pointer bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded"
                      onClick={() => setEditingQuantity(item._id)}
                      title={t("tapToEditQuantity")}
                    >
                      {item.quantity}
                    </button>
                  )}

                  <Button
                    type="primary"
                    size="small"
                    className="!rounded-full"
                    icon={<MinusCircleOutlined />}
                    onClick={() => {
                      if (item.quantity === 1) {
                        Modal.confirm({
                          title: t("deleteProductModalTitle"),
                          content: t("deleteProductModalContent"),
                          okText: tCommon("yes"),
                          cancelText: tCommon("no"),
                          onOk: () => {
                            dispatch(decrease(item));
                          },
                        });
                      } else if (item.quantity > 1) {
                        dispatch(decrease(item));
                      }
                    }}
                  />
                </div>
              </li>
            ))
            .reverse()
        ) : (
          <div className="text-center mt-4 font-medium text-gray-600 text-sm">
            {t("noItemsInCart")}
          </div>
        )}
      </ul>

      {/* Totals + actions */}
      <div className="cart-totals mt-auto border-t bg-white">
        <div className="border-b px-3 py-2 space-y-1">
          <div className="flex justify-between text-sm">
            <span className="font-medium text-gray-700">{t("subtotal")}</span>
            <span className="text-gray-800">{subTotal.toFixed(2)}</span>
          </div>
          {showDiscountField && totalDiscount > 0 && (
            <div className="flex justify-between text-sm">
              <span className="font-medium text-gray-700">{t("discountLabel")}</span>
              <span className="text-green-600">-{totalDiscount.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <span className="font-medium text-gray-700">{t("govtTaxes")}</span>
            <span className="text-red-700">+{totalTax.toFixed(2)}</span>
          </div>
        </div>

        <div className="border-b px-3 py-3">
          <div className="flex justify-between items-center">
            <b className="text-base font-semibold text-gray-800">{t("grandTotal")}</b>
            <span className="text-lg font-semibold text-gray-800">{grandTotal.toFixed(2)}</span>
          </div>
        </div>

        {/* Payment method (moved from Create Invoice modal) */}
        <div className="border-b px-3 py-3">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm font-medium text-gray-700">
              {t("paymentMethod")}
            </span>
            <Select
              size="middle"
              className="min-w-[170px]"
              value={selectedPaymentMode}
              onChange={(val) => dispatch(setPaymentMode(val))}
              placeholder={t("selectPayment")}
              options={[
                { label: PAYMENT_MODE_CASH, value: PAYMENT_MODE_CASH },
                { label: PAYMENT_MODE_DEBIT_CARD, value: PAYMENT_MODE_DEBIT_CARD },
                { label: PAYMENT_MODE_CREDIT, value: PAYMENT_MODE_CREDIT },
              ]}
            />
          </div>
        </div>

        <div className="py-4 px-3 space-y-2">
          <Button
            type="primary"
            size="large"
            className="w-full font-semibold"
            disabled={cartItems.length === 0}
            onClick={handleCreateInvoice}
          >
            {t("createInvoice")}
          </Button>

          {cartItems.length > 0 && (
            <Popconfirm
              title={t("clearBasket")}
              description={t("clearBasketConfirm")}
              okText={tCommon("yes")}
              cancelText={tCommon("no")}
              onConfirm={() => dispatch(reset())}
              className="w-full flex items-center justify-center"
            >
              <Button
                type="primary"
                size="large"
                className="w-full flex items-center justify-center"
                icon={<ClearOutlined />}
                danger
              >
                {t("clearBasket")}
              </Button>
            </Popconfirm>
          )}
        </div>
      </div>
    </div>
  );
};

export default CartTotals;
